import React, { useMemo } from 'react';
import _ from 'lodash';
import { useStateSelector } from '../../../../data/global/dataSelectors';
import { relationshipSelectors } from '../../../../data/global/selectors/admin/cashieringSelectors';
import { CustomerAccountDetails } from './CustomerAccountDetails';
import styled from 'styled-components';
import { DetailField, DetailFields } from '../../../../components/basic/DetailFields';

// Styled components matching the admin cashiering detail component
const SectionCard = styled.div`
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`;

const SectionContent = styled.div`
  padding: var(--gap);
  background: var(--blue-dark);
  color: #fff;

  /* Consistent text styling */
  label {
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
    font-size: 0.98rem;
  }

  .mute {
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.98rem;
    font-weight: 400;
  }

  /* Override prop-title and prop-value styling for cashiering */
  .prop-title {
    font-size: 0.98rem;
    font-weight: 600;
    color: #fff;
    margin-right: 0.5rem;
  }

  .prop-value {
    font-size: 0.98rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }

  /* Ensure prop-stacked layout works correctly */
  .prop-stacked {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--gap);
  background: var(--blue-dark);
  border-radius: var(--radius);
  border: 1px solid var(--lighten);

  .loading-text {
    margin-left: 0.75rem;
    color: rgba(255, 255, 255, 0.85);
    font-size: 0.75rem;
  }
`;

const RelationshipInfoHeader = styled.div`
  padding: 0 0 var(--gap) 0;
  border-bottom: 1px solid var(--lighten);
  margin-bottom: var(--gap);

  h3 {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 300;
    color: #fff;
    display: flex;
    align-items: center;
    gap: 0.5em;
    text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  }
`;

const RelationshipInfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--gap);
`;

const ResponsiveGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: var(--gap);
  width: 100%;
  margin: 0;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin: 0;
  padding: 0;

  .prop-title {
    font-size: 0.9rem;
    font-weight: 600;
    color: #fff;
    margin-bottom: 0.25rem;
  }

  .prop-value {
    font-size: 0.9rem;
    font-weight: 400;
    color: rgba(255, 255, 255, 0.85);
    line-height: 1.4;
  }
`;

// Utility to format status strings (e.g., PendingCreateApproval -> Pending Create Approval)
function formatStatus(status: string) {
  return status.replace(/([a-z])([A-Z])/g, '$1 $2');
}

export const CashieringRelationshipDetails: React.FunctionComponent<{
  id: number;
  accountId: number;
  isFetching: boolean;
}> = ({ id, accountId, isFetching }) => {
  const relationship = useStateSelector(s => relationshipSelectors.data(s, id));
  const relationshipDetails = useStateSelector(s => relationshipSelectors.details(s, id));
  const isFetchingDetails = useStateSelector(
    s => s.private.cashiering.relationshipDetails[id] && s.private.cashiering.relationshipDetails[id].isFetching
  );

  // Compose fields for the relationship details
  const detailRows = useMemo(() => {
    if (!(relationship && relationshipDetails)) return [];
    const rows = [
      ['Bank Name', relationship.bankName],
      ['Bank Account Number', relationship.accountNumber],
      ['Account Type', relationship.bankAccountType],
      ['Routing Number', relationship.bankRoutingNumber],
      ['Status', relationship.state ? formatStatus(relationship.state) : undefined],
      ['Create Date', relationship.creationTime],
    ].filter(([, value]) => value !== undefined && value !== null && value !== '');
    return rows;
  }, [relationship, relationshipDetails]);

  return (
    <>
      {(isFetching || isFetchingDetails) && (
        <LoadingContainer>
          <div className="loading-text">Loading relationship details...</div>
        </LoadingContainer>
      )}
      {!isFetching && !isFetchingDetails && (
        <div className="section-body">
          <RelationshipInfoHeader>
            <h3>
              <i className="fal fa-handshake" style={{ marginRight: '8px' }}></i>
              Relationship Details
            </h3>
          </RelationshipInfoHeader>
          <RelationshipInfoContent>
            <ResponsiveGrid>
              {detailRows.map(([label, value]: [string, string], i: number) => (
                <FieldGroup key={i}>
                  <div className="prop-title">{label}</div>
                  <div className="prop-value">{value}</div>
                </FieldGroup>
              ))}
            </ResponsiveGrid>
          </RelationshipInfoContent>

          <CustomerAccountDetails
            accountId={accountId}
            relationship={relationship}
            relationshipDetails={relationshipDetails}
            useCardLayout={true}
          />
        </div>
      )}
    </>
  );
};
